import { useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useSelector } from 'react-redux'

import { Tooltip } from '@/components/ui'
import { selectIfUserIsAdmin } from '@/features/auth/authSlice'

const STATUS_BADGES = {
  cancelled: { label: 'Annulée', className: 'badge-ghost' }
}

/**
 * Composant unifié pour afficher les tâches (ProcessTask et RenewTask)
 *
 * @param {Object} task - Tâche à afficher
 * @param {Function} onComplete - Callback lors de la complétion
 * @param {Function} onStart - Callback lors du démarrage (optionnel, pour ProcessTasks)
 * @param {string} type - Type de tâche: 'renew' | 'process'
 * @param {boolean} showColorIndicator - Afficher le cercle de couleur (défaut: false)
 * @param {boolean} showUrgencyBadge - Afficher le badge d'urgence (défaut: true)
 * @param {boolean} showDetails - Afficher les détails supplémentaires (défaut: false)
 */
const TaskItem = ({
  task,
  bienLabel = "",
  onComplete,
  // onStart, // 2026-06-14 Commenté car pour le moment les tâches n'ont pas besoin d'être déclarée commencée, puis terminée.
  onValidate,
  onReopen,
  type = 'process',
  showColorIndicator = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const isAdmin = useSelector(selectIfUserIsAdmin)

  // États pour la confirmation de check et affichage de la description
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showDescription, setShowDescription] = useState(false)

  // Confirmer la complétion de la tâche
  const confirmCompletion = async () => {
    setIsUpdating(true)
    try {
      // 2026-06-14 Commenté car pour le moment les tâches n'ont pas besoin d'être déclarée commencée, puis terminée.
      // Si la tâche est en attente et qu'il y a onStart, d'abord la démarrer
      // if (onStart && task.status === 'pending') {
      //   await onStart(task.id)
      // }
      await onComplete(task.id)
    } finally {
      setIsUpdating(false)
      setShowConfirmDialog(false)
    }
  }

  // Annuler la suppression
  const cancelCompletion = () => {
    setIsUpdating(false)
    setShowConfirmDialog(false)
  }

  const handleCheckboxChange = async (e) => {
    e.preventDefault()

    if (task.status === 'completed' || task.status === 'cancelled' || task.status === 'validated') return

    setShowConfirmDialog(true)
  }

  const handleValidate = async () => {
    if (!onValidate || isUpdating) return
    setIsUpdating(true)
    try {
      await onValidate(task.id)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleReopen = async () => {
    if (!onReopen || isUpdating) return
    setIsUpdating(true)
    try {
      await onReopen(task.id)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleOpenDescriptionModal = () => {
    setShowDescription(true)
  }

  const isCompleted = task.status === 'completed'
  const isCancelled = task.status === 'cancelled'
  const isValidated = task.status === 'validated'
  const isDisabled = isUpdating || isCancelled || isValidated

  // Cercle de couleur avec la couleur de l'utilisateur assigné
  const getColorIndicator = () => {
    if (!showColorIndicator) return null

    // Utiliser la couleur de l'utilisateur assigné, gris si inactif
    const userColor = task.assigned_to?.is_active === false
      ? '#9CA3AF'
      : (task.assigned_to?.color || '#9CA3AF')

    const userName = !task.assigned_to
      ? 'Non assigné'
      : task.assigned_to.is_active === false
        ? `${task.assigned_to.full_name} (Utilisateur non disponible)`
        : task.assigned_to.full_name

    return (
      <Tooltip 
        content={userName}
        className='flex items-center gap-2'
        color="primary"
        position="left"
      >
        <div
          className="w-6 h-6 rounded-full flex-shrink-0 border-2 border-white shadow-sm"
          style={{ backgroundColor: userColor }}
        />
      </Tooltip>
    )
  } 

  const getTitle = () => task.title

  const hasDescription = () => task.description?.length > 0

  const getDescription = () => {
    if(task.description?.length > 0 ) {
      return <pre>{task.description}</pre>
    }
    return null
  }

  const statusBadge = STATUS_BADGES[task.status] || null

  return (
    <div className={`flex-col space-y-2 items-center p-2 rounded transition-colors border ${
      isValidated
        ? 'bg-emerald-100 border-emerald-300'
        : isCompleted
          ? 'bg-green-50 border-green-200'
          : isCancelled
            ? 'bg-gray-50 border-gray-200 opacity-60'
            : 'bg-white border-gray-200 hover:bg-gray-50'
    }`}>
      <div className='flex items-center'>
      {/* Checkbox */}
        <input
          type="checkbox"
          className={`checkbox mt-1 flex-shrink-0 ${
            isValidated ? 'checkbox-success' :
            isCompleted ? 'checkbox' : 'checkbox-primary'
          }`}
          checked={isCompleted || isValidated}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
        />

        <div className="flex-1 min-w-0 ml-3">

          <div className="flex">
            <div className={`flex-1 font-medium ${
              isCancelled ? 'text-gray-600' : 'text-gray-900'
            } flex flex-nowrap items-center gap-2`}>
              { getTitle() }
              { hasDescription() && <Tooltip text="Afficher la description" className="cursor-pointer" color="primary">
                  <InformationCircleIcon className='h-4 w-4' onClick={handleOpenDescriptionModal} />
                </Tooltip> }
              { statusBadge && 
                <span className={`badge ${statusBadge.className} badge-sm mx-2`}>{statusBadge.label}</span>
              }
            </div>

            <div className="flex-shrink-0 ml-2 flex items-center gap-2 flex-wrap">
              {getColorIndicator()}
              {/* Boutons admin pour validate/reopen */}
              {isAdmin && isCompleted && !isValidated && onValidate && (
                <button
                  onClick={handleValidate}
                  disabled={isUpdating}
                  className="btn btn-xs btn-success"
                  title="Valider la tâche"
                >
                  {isUpdating ? <span className="loading loading-spinner loading-xs" /> : 'Valider'}
                </button>
              )}
              {isAdmin && isCompleted && onReopen && (
                <button
                  onClick={handleReopen}
                  disabled={isUpdating}
                  className="btn btn-xs btn-warning"
                  title="Rouvrir la tâche"
                >
                  {isUpdating ? <span className="loading loading-spinner loading-xs" /> : 'Rouvrir'}
                </button>
              )}
            </div>

          </div>

        </div>
      </div>

      {(!isCompleted && showConfirmDialog) && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmer</h3>
            <p className="py-4">"{getTitle()} est-elle réalisée ?</p>
            <div className="modal-action">
              <button onClick={confirmCompletion} className="btn btn-secondary">Confirmer</button>
              <button onClick={cancelCompletion} className="btn btn-outline">Annuler</button>
            </div>
          </div>
        </dialog>
      )}

      {(hasDescription() && showDescription) && (
        <dialog open className="modal">
          <div className="modal-box max-w-1/2">
            <h3 className="font-bold text-lg flex-col gap-2">
              <p className="text-sm text-gray-600">{ bienLabel?.length > 0 && bienLabel }</p>
              <p>{ getTitle() } - Description</p>
            </h3>
            <div className="py-4">{getDescription()}</div>
            <div className="modal-action">
              <button onClick={() => setShowDescription(false)} className="btn btn-outline">Annuler</button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  )
}

export default TaskItem
